/**
 * Voice-note recorder — real `MediaRecorder` capture with a live waveform.
 *
 *   const rec = useVoiceRecorder()
 *   await rec.start()                       // prompts for mic permission
 *   const clip = await rec.stop()           // { file, durationMs } | null
 *   rec.cancel()                            // discard, release the mic
 *
 * The waveform is sampled from an `AnalyserNode` on the live stream, so the
 * bars track what the mic is actually hearing rather than animating randomly.
 *
 * Every exit path — stop, cancel, unmount, an error mid-record — stops the
 * MediaStream tracks. Skipping that leaves the browser's recording indicator
 * lit and the mic held open.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

const BARS = 28

export interface VoiceClip {
  file: File
  durationMs: number
}

export interface UseVoiceRecorder {
  isRecording: boolean
  /** Whole seconds elapsed — drives the running time. */
  seconds: number
  /** `BARS` normalized levels in 0..1. */
  waveform: number[]
  /** Set when the mic can't be opened (denied, no device, insecure origin). */
  error: string | null
  start: () => Promise<boolean>
  stop: () => Promise<VoiceClip | null>
  cancel: () => void
}

/** First mime the browser will actually record; codec support varies a lot. */
function pickMime(): string {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ]
  for (const mime of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mime)) return mime
  }
  return ''
}

function extensionFor(mime: string): string {
  if (mime.includes('ogg')) return 'ogg'
  if (mime.includes('mp4')) return 'm4a'
  return 'webm'
}

export function useVoiceRecorder(): UseVoiceRecorder {
  const [isRecording, setIsRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [waveform, setWaveform] = useState<number[]>(() => Array(BARS).fill(0.15))
  const [error, setError] = useState<string | null>(null)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const audioCtxRef = useRef<AudioContext | null>(null)
  const rafRef = useRef<number | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAtRef = useRef<number>(0)

  const teardown = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    if (tickRef.current) clearInterval(tickRef.current)
    tickRef.current = null
    audioCtxRef.current?.close().catch(() => {})
    audioCtxRef.current = null
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    recorderRef.current = null
  }, [])

  // A component unmounting mid-record must not leave the mic open.
  useEffect(() => teardown, [teardown])

  const startMeter = useCallback((stream: MediaStream) => {
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new Ctor()
      audioCtxRef.current = ctx
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      const buf = new Uint8Array(analyser.frequencyBinCount)

      const sample = () => {
        analyser.getByteFrequencyData(buf)
        // RMS over the spectrum → one level per frame, pushed through the bars.
        let sum = 0
        for (let i = 0; i < buf.length; i += 1) sum += buf[i] * buf[i]
        const level = Math.min(1, Math.sqrt(sum / buf.length) / 128)
        setWaveform((prev) => [...prev.slice(1), Math.max(0.15, level)])
        rafRef.current = requestAnimationFrame(sample)
      }
      rafRef.current = requestAnimationFrame(sample)
    } catch {
      // Metering is decorative — recording continues without it.
    }
  }, [])

  const start = useCallback(async (): Promise<boolean> => {
    setError(null)
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Recording is not supported in this browser')
      return false
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mime = pickMime()
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
      chunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }
      recorder.start()
      recorderRef.current = recorder

      startedAtRef.current = Date.now()
      setSeconds(0)
      setWaveform(Array(BARS).fill(0.15))
      setIsRecording(true)
      tickRef.current = setInterval(() => {
        setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000))
      }, 250)
      startMeter(stream)
      return true
    } catch (err) {
      const name = (err as { name?: string })?.name
      setError(
        name === 'NotAllowedError'
          ? 'Microphone permission denied'
          : name === 'NotFoundError'
            ? 'No microphone found'
            : 'Could not start recording',
      )
      teardown()
      return false
    }
  }, [startMeter, teardown])

  const stop = useCallback((): Promise<VoiceClip | null> => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') {
      teardown()
      setIsRecording(false)
      return Promise.resolve(null)
    }

    return new Promise<VoiceClip | null>((resolve) => {
      recorder.onstop = () => {
        const durationMs = Date.now() - startedAtRef.current
        const type = recorder.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type })
        chunksRef.current = []
        teardown()
        setIsRecording(false)
        setSeconds(0)
        setWaveform(Array(BARS).fill(0.15))

        // Sub-second taps are almost always an accidental press, not a message.
        if (blob.size === 0 || durationMs < 500) {
          resolve(null)
          return
        }
        const name = `voice-${Date.now()}.${extensionFor(type)}`
        resolve({ file: new File([blob], name, { type }), durationMs })
      }
      recorder.stop()
    })
  }, [teardown])

  const cancel = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null
      try {
        recorder.stop()
      } catch {
        // Already stopping — teardown below still releases the mic.
      }
    }
    chunksRef.current = []
    teardown()
    setIsRecording(false)
    setSeconds(0)
    setWaveform(Array(BARS).fill(0.15))
  }, [teardown])

  return { isRecording, seconds, waveform, error, start, stop, cancel }
}
